import { NgModule } from '@angular/core'
import { PreloadAllModules, RouterModule, Routes } from '@angular/router'
import { AuthGuardController as AuthGuard } from './controllers/auth-guard.controller'
import { featureCanMatch } from './controllers/feature-guard.controller'
import { FeatureKeys } from './features/feature-keys.generated'

const routes: Routes = [
  {
    path: '',
    redirectTo: 'overview?1',
    pathMatch: 'full',
  },
  {
    path: 'login',
    loadChildren: () => import('./pages/login/login.module').then(m => m.LoginPageModule),
  },
  {
    path: 'magic-link-login/:token',
    loadChildren: () =>
      import('./pages/magic-link-login/magic-link-login.module').then(m => m.MagicLinkLoginPageModule),
  },
  // La surface EMPLOYE de l'accueil virtuel. Deux chemins, un seul composant,
  // et AUCUN garde -- ni `canActivate`, ni `canMatch`.
  //
  // Pas de `AuthGuard` : celui qui ouvre cet ecran n'a aucun compte Cordero. Son
  // seul justificatif est le jeton du SMS, qu'il echange contre un cookie de
  // session. C'est le meme patron que `magic-link-login/:token` juste au-dessus.
  //
  // Pas de `featureCanMatch` non plus, contrairement a `formations` : le garde
  // redirigerait vers `/` chez un locataire sans `sidebar.formations`, donc vers
  // la page de connexion administrateur -- incomprehensible pour un employe qui
  // vient de recevoir un lien. Les routes de signature portent `@requires_feature`
  // cote API ; l'employe doit voir l'echec reel (404) et l'ecran qui l'explique.
  //
  // `signature` sans jeton n'est pas une faute de frappe : c'est l'URL qui reste
  // apres que l'echange a efface le jeton par `replaceState`. Un rechargement,
  // un retour arriere ou une reprise le jour suivant y atterrissent, et le
  // composant repart alors du cookie via `GET /signature/session`.
  //
  // La route a DEUX segments est declaree d'abord, par prudence et non par
  // necessite -- et la nuance a ete verifiee plutot que supposee. `path:
  // 'signature'` s'apparie par PREFIXE (le defaut) : place en premier, il avale
  // le segment `signature` de `signature/<jeton>` et doit ensuite trouver
  // `<jeton>` parmi les routes du module charge, qui ne declare que `path: ''`.
  // Angular 17 REVIENT EN ARRIERE dans ce cas et essaie la route suivante :
  // l'ordre inverse a ete essaye a HEAD et fonctionne aussi. Le specifique
  // passe quand meme devant, comme `formations/create/:id` devant
  // `formations/:id/dashboard` plus bas : la regle ne depend alors plus d'un
  // detail du routeur, et un futur `pathMatch` ajoute sur la route courte ne
  // peut plus casser le lien du SMS.
  {
    path: 'signature/:token',
    loadChildren: () => import('./pages/signature/signature.module').then(m => m.SignaturePageModule),
  },
  {
    path: 'signature',
    loadChildren: () => import('./pages/signature/signature.module').then(m => m.SignaturePageModule),
  },
  {
    path: 'forgot-password',
    loadChildren: () =>
      import('./pages/forgot-password/forgot-password.module').then(m => m.ForgotPasswordPageModule),
  },
  {
    path: 'change-password/:token',
    loadChildren: () =>
      import('./pages/change-password/change-password.module').then(m => m.ChangePasswordPageModule),
  },
  {
    path: 'overview',
    loadChildren: () => import('./pages/overview/overview.module').then(m => m.OverviewPageModule),
    canActivate: [AuthGuard],
    data: { roles: ['admin'] },
  },
  {
    path: 'matching',
    children: [
      {
        path: '',
        loadChildren: () => import('./pages/matching/matching.module').then(m => m.MatchingPageModule),
      },
      {
        path: 'create/:orderId',
        loadChildren: () =>
          import('./pages/create-matching/create-matching.module').then(m => m.CreateMatchingPageModule),
      },
      {
        path: 'create/:orderId/:assignmentId',
        loadChildren: () =>
          import('./pages/create-matching/create-matching.module').then(m => m.CreateMatchingPageModule),
      },
    ],
    canActivate: [AuthGuard],
    data: { roles: ['admin'] },
  },
  {
    path: 'orders',
    children: [
      {
        path: '',
        loadChildren: () => import('./pages/orders/orders.module').then(m => m.OrdersPageModule),
      },
      {
        path: 'create/:id',
        loadChildren: () => import('./pages/order/order.module').then(m => m.OrderPageModule),
      },
      {
        path: ':id',
        loadChildren: () => import('./pages/order/order.module').then(m => m.OrderPageModule),
      },
    ],
    canActivate: [AuthGuard],
    data: { roles: ['admin'] },
  },
  {
    path: 'employees',
    children: [
      {
        path: '',
        loadChildren: () => import('./pages/employees/employees.module').then(m => m.EmployeesPageModule),
      },
      {
        path: 'create',
        loadChildren: () =>
          import('./pages/create-employee/create-employee.page.module').then(m => m.CreateEmployeePageModule),
      },
      {
        path: ':id',
        loadChildren: () =>
          import('./pages/employee-view/employee-view.page.module').then(m => m.EmployeeViewPageModule),
      },
      {
        path: 'edit/:id',
        loadChildren: () =>
          import('./pages/create-employee/create-employee.page.module').then(m => m.CreateEmployeePageModule),
      },
    ],
    canActivate: [AuthGuard],
    data: { roles: ['admin'] },
  },
  {
    path: 'supervisor',
    children: [
      {
        path: '',
        loadChildren: () => import('./pages/supervisor/supervisor.module').then(m => m.SupervisorPageModule),
      },
      {
        path: 'create',
        loadChildren: () =>
          import('./pages/create-supervisor/create-supervisor.page.module').then(
            m => m.CreateSupervisorPageModule
          ),
      },
      {
        path: ':id',
        loadChildren: () =>
          import('./pages/supervisor-view/supervisor-view.module').then(m => m.SupervisorViewPageModule),
      },
      {
        path: 'edit/:id',
        loadChildren: () =>
          import('./pages/create-supervisor/create-supervisor.page.module').then(
            m => m.CreateSupervisorPageModule
          ),
      },
    ],
    canActivate: [AuthGuard],
    data: { roles: ['admin'] },
  },
  {
    path: 'clients',
    children: [
      {
        path: '',
        loadChildren: () => import('./pages/clients/clients.module').then(m => m.ClientsPageModule),
      },
      {
        path: 'create',
        loadChildren: () =>
          import('./pages/create-client/create-client.page.module').then(m => m.CreateClientPageModule),
      },
      {
        path: ':id',
        loadChildren: () => import('./pages/client/client.module').then(m => m.ClientPageModule),
      },
      {
        path: 'edit/:id',
        loadChildren: () =>
          import('./pages/create-client/create-client.page.module').then(m => m.CreateClientPageModule),
      },
    ],
    canActivate: [AuthGuard],
    data: { roles: ['admin'] },
  },
  {
    path: 'clients-dashboard',
    loadChildren: () =>
      import('./pages/clients-dashboard/clients-dashboard.module').then(m => m.ClientsDashboardModule),
    canActivate: [AuthGuard],
    data: { roles: ['admin'] },
  },
  {
    path: 'messages',
    canMatch: [featureCanMatch],
    children: [
      { path: '', redirectTo: '/messages/general', pathMatch: 'full' },
      {
        path: 'general',
        loadChildren: () => import('./pages/messages/messages.module').then(m => m.MessagesPageModule),
      },
      {
        path: 'assignation',
        loadChildren: () => import('./pages/messages/messages.module').then(m => m.MessagesPageModule),
      },
      {
        path: 'applicants',
        loadChildren: () => import('./pages/messages/messages.module').then(m => m.MessagesPageModule),
      },
      {
        path: 'create',
        loadChildren: () =>
          import('./pages/create-message/create-message.module').then(m => m.CreateMessagePageModule),
      },
      {
        path: 'edit/:id',
        loadChildren: () =>
          import('./pages/create-message/create-message.module').then(m => m.CreateMessagePageModule),
      },
      {
        path: ':id',
        loadChildren: () =>
          import('./pages/conversation/conversation.module').then(m => m.ConversationPageModule),
      },
    ],
    canActivate: [AuthGuard],
    data: { roles: ['admin'], feature: FeatureKeys.SIDEBAR_COMMUNICATIONS },
  },
  {
    path: 'skills',
    canMatch: [featureCanMatch],
    children: [
      {
        path: '',
        loadChildren: () => import('./pages/skills/skills.page.module').then(m => m.SkillsPageModule),
      },
      {
        path: 'create',
        loadChildren: () =>
          import('./pages/create-skill/create-skill.page.module').then(m => m.CreateSkillPageModule),
      },
      {
        path: ':id',
        loadChildren: () => import('./pages/skill-view/skill-view.page.module').then(m => m.SkillViewModule),
      },
      {
        path: 'edit/:id',
        loadChildren: () =>
          import('./pages/create-skill/create-skill.page.module').then(m => m.CreateSkillPageModule),
      },
    ],
    canActivate: [AuthGuard],
    data: { roles: ['admin'], feature: FeatureKeys.SIDEBAR_SKILLS },
  },
  {
    path: 'tools',
    children: [
      {
        path: '',
        loadChildren: () => import('./pages/tools/tools.module').then(m => m.ToolsPageModule),
      },
      {
        path: 'create',
        loadChildren: () =>
          import('./pages/create-tool/create-tool.page.module').then(m => m.CreateToolPageModule),
      },
      {
        path: ':id',
        loadChildren: () => import('./pages/tool-view/tool-view.page.module').then(m => m.ToolViewPageModule),
      },
      {
        path: 'edit/:id',
        loadChildren: () =>
          import('./pages/create-tool/create-tool.page.module').then(m => m.CreateToolPageModule),
      },
    ],
    canActivate: [AuthGuard],
    data: { roles: ['admin'] },
  },
  {
    path: 'timesheets',
    children: [
      {
        path: '',
        loadChildren: () => import('./pages/timesheets/timesheets.module').then(m => m.TimeSheetsPageModule),
        canActivate: [AuthGuard],
        data: { roles: ['admin'] },
      },
      {
        path: 'create',
        loadChildren: () =>
          import('./pages/create-timesheet/create-timesheet.module').then(m => m.CreateTimesheetPageModule),
        canActivate: [AuthGuard],
        data: { roles: ['admin'] },
      },
      {
        path: 'edit/:id',
        loadChildren: () =>
          import('./pages/create-timesheet/create-timesheet.module').then(m => m.CreateTimesheetPageModule),
        canActivate: [AuthGuard],
        data: { roles: ['admin', 'supervisor'] },
      },
    ],
  },
  {
    path: 'dashboard-supervisor',
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./pages/dashboard-supervisor/dashboard-supervisor.module').then(
            m => m.DashboardSupervisorPageModule
          ),
      },
    ],
    canActivate: [AuthGuard],
    data: { roles: ['supervisor'] },
  },
  {
    path: 'dashboard',
    children: [
      {
        path: 'map',
        loadChildren: () =>
          import('./pages/dashboard-map/dashboard-map.module').then(m => m.DashboardMapPageModule),
      },
      {
        path: 'map/:id',
        loadChildren: () =>
          import('./pages/dashboard-map/dashboard-map.module').then(m => m.DashboardMapPageModule),
      },
    ],
    canActivate: [AuthGuard],
    data: { roles: ['admin'] },
  },
  {
    path: 'time-entries',
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./pages/time-entries/time-entries.module').then(m => m.TimeEntriesPageModule),
      },
      {
        path: ':employeeId',
        loadChildren: () =>
          import('./pages/time-entries/time-entries.module').then(m => m.TimeEntriesPageModule),
      },
    ],
    canActivate: [AuthGuard],
    data: { roles: ['admin', 'employee', 'supervisor'] },
  },
  {
    path: 'time-logger',
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./pages/time-logger/time-logger.module').then(m => m.TimeLoggerPageModule),
      },
      {
        path: ':timeEntryId',
        loadChildren: () =>
          import('./pages/time-logger/time-logger.module').then(m => m.TimeLoggerPageModule),
      },
    ],
    canActivate: [AuthGuard],
    data: { roles: ['admin', 'employee', 'supervisor'] },
  },
  {
    path: 'absences',
    children: [
      {
        path: '',
        loadChildren: () => import('./pages/absences/absences.module').then(m => m.AbsencesPageModule),
      },
      {
        path: 'create',
        loadChildren: () =>
          import('./pages/create-tool/create-tool.page.module').then(m => m.CreateToolPageModule),
      },
      {
        path: 'edit/:id',
        loadChildren: () =>
          import('./pages/create-tool/create-tool.page.module').then(m => m.CreateToolPageModule),
      },
    ],
    canActivate: [AuthGuard],
    data: { roles: ['admin'] },
  },
  {
    path: 'alerts',
    canMatch: [featureCanMatch],
    children: [
      {
        path: '',
        loadChildren: () => import('./pages/alerts/alerts.module').then(m => m.AlertsPageModule),
      },
    ],
    canActivate: [AuthGuard],
    data: { roles: ['admin'], feature: FeatureKeys.SIDEBAR_ALERTS },
  },
  {
    path: 'hour-alerts',
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./pages/hour-alerts/hour-alerts.module').then(m => m.HourAlertsPageModule),
      },
    ],
    canActivate: [AuthGuard],
    data: { roles: ['admin'] },
  },
  {
    path: 'dashboard-employees',
    loadChildren: () =>
      import('./pages/dashboard-employees/dashboard-employees.module').then(m => m.EmployeesDashboardModule),
    canActivate: [AuthGuard],
    data: { roles: ['admin'] },
  },
  {
    path: 'applicants',
    children: [
      {
        path: '',
        loadChildren: () => import('./pages/applicants/applicants.module').then(m => m.ApplicantsPageModule),
      },
      {
        path: 'create',
        loadChildren: () =>
          import('./pages/create-applicant/create-applicant.module').then(m => m.CreateApplicantPageModule),
      },
    ],
    canActivate: [AuthGuard],
    data: { roles: ['admin'] },
  },
  {
    path: 'dashboard-no-orders',
    loadChildren: () =>
      import('./pages/dashboard-no-orders/dashboard-no-orders.module').then(
        m => m.DashboardNoOrdersPageModule
      ),
    canActivate: [AuthGuard],
    data: { roles: ['admin'] },
  },
  {
    path: 'dashboard-no-orders-stats',
    loadChildren: () =>
      import('./pages/dashboard-no-orders-stats/dashboard-no-orders-stats.module').then(
        m => m.DashboardNoOrdersStatsPageModule
      ),
    canActivate: [AuthGuard],
    data: { roles: ['admin'] },
  },
  {
    path: 'dashboard-employee-call',
    loadChildren: () =>
      import('./pages/dashboard-employee-call/dashboard-employee-call.module').then(
        m => m.DashboardEmployeeCallPageModule
      ),
    canActivate: [AuthGuard],
    data: { roles: ['admin'] },
  },
  {
    path: 'dashboard-employee-call-stats',
    loadChildren: () =>
      import('./pages/dashboard-employee-call-stats/dashboard-employee-call-stats.module').then(
        m => m.DashboardEmployeeCallStatsPageModule
      ),
    canActivate: [AuthGuard],
    data: { roles: ['admin'] },
  },
  {
    path: 'template-docs',
    canMatch: [featureCanMatch],
    loadChildren: () =>
      import('./pages/template-docs/template-docs.module').then(m => m.TemplateDocsPageModule),
    canActivate: [AuthGuard],
    data: { roles: ['admin'], feature: FeatureKeys.SIDEBAR_TEMPLATE_DOCS },
  },
  {
    path: 'formations',
    canMatch: [featureCanMatch],
    children: [
      {
        path: '',
        loadChildren: () => import('./pages/formations/formations.module').then(m => m.FormationsPageModule),
      },
      {
        path: 'create',
        loadChildren: () =>
          import('./pages/create-formation/create-formation.page.module').then(
            m => m.CreateFormationPageModule
          ),
      },
      // Reprise d'un brouillon : la formation existe deja en base, et le
      // presentateur qui est alle corriger une fiche employe revient ici.
      {
        path: 'create/:id',
        loadChildren: () =>
          import('./pages/create-formation/create-formation.page.module').then(
            m => m.CreateFormationPageModule
          ),
      },
      // Le suivi en direct : l'ecran que le presentateur garde ouvert pendant
      // toute la seance. Declare APRES `create/:id` -- un chemin litteral
      // d'abord, le parametre ensuite -- pour que `formations/create/x` ne
      // puisse jamais se lire comme une seance nommee << create >>.
      {
        path: ':id/dashboard',
        loadChildren: () =>
          import('./pages/formation-dashboard/formation-dashboard.page.module').then(
            m => m.FormationDashboardPageModule
          ),
      },
    ],
    canActivate: [AuthGuard],
    data: { roles: ['admin'], feature: FeatureKeys.SIDEBAR_FORMATIONS },
  },
  { path: '**', redirectTo: '/' },
]

@NgModule({
  imports: [RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
